<template>
  <div
    class="card-component"
    :class="{ 'clickable': isClickable }"
    :style="styleCard"
    @click="cardClicked"
  >
    <div class="card-content">
      <div
        class="selected-overlay"
        :class="{ 'selected': isSelected || isForbidden }"
      >
        <v-img
          :src="card.pictureUrl"
          :lazy-src="cardDefault"
          alt="card picture"
          :class="['card-image', {'card-image--error': isError}]"
          @mouseover="isHover = true"
          @mouseleave="isHover = false"
        >
          <captain-btn
            v-if="showCaptain"
            :is-captain="isCaptain"
            @captainSelected="captainSelected"
          />
          <remove-card-btn
            v-if="isHover && isRemovable"
            @removeCard="$emit('removeCard')"
          />
        </v-img>
        <font-awesome-icon
          v-if="isSelected || isForbidden"
          :icon="['fas', cardIcon]"
          size="2x"
          class="selected-icon"
        />
        <fixture
          v-if="showFixture && card.fixture"
          :home-team="card.fixture.homeTeam"
          :away-team="card.fixture.awayTeam"
          :card-height="cardHeight"
        />
      </div>

      <div class="card-score">
        <v-tooltip
          :disabled="!showSplittedScore || card.totalScore === null"
          location="bottom"
          color="background"
          open-delay="500"
        >
          <template #activator="{ props }">
            <score
              v-if="showScore"
              v-bind="props"
              :score="card.totalScore"
              :short="true"
              size="md"
            />
          </template>
          <splitted-score
            :decisive-score="card.decisiveScore"
            :all-around-score="parseFloat(card.allAroundScore)"
          />
        </v-tooltip>
      </div>

      <average-score
        v-if="showAverageScore"
        :average-score="card.player.last15Average"
        :size="size"
      />

      <div
        v-if="showLastScores"
        class="last-scores"
      >
        <score
          v-for="(score, index) in card.player.scores"
          :key="index"
          :score="score"
          :short="true"
          size="sm"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CaptainBtn from '@/components/Card/CaptainBtn.vue';
import Score from '@/components/Card/Score.vue';
import AverageScore from '@/components/Card/AverageScore.vue';
import Fixture from '@/components/Card/Fixture.vue';
import SplittedScore from '@/components/Card/SplittedScore.vue';
import Card from '@/models/Card/card';
import cardDefault from '@/assets/card-default.png';
import RemoveCardBtn from '@/components/Card/RemoveCardBtn.vue';

export default {
  name: 'CardViewer',
  components: {
    CaptainBtn,
    Score,
    AverageScore,
    SplittedScore,
    Fixture,
    RemoveCardBtn,
  },
  props: {
    card: {
      type: Card,
      required: true,
    },
    width: {
      type: String,
      default: '150px',
    },
    showLastScores: {
      type: Boolean,
      default: false,
    },
    showScore: {
      type: Boolean,
      default: false,
    },
    showSplittedScore: {
      type: Boolean,
      default: false,
    },
    showAverageScore: {
      type: Boolean,
      default: false,
    },
    showFixture: {
      type: Boolean,
      default: false,
    },
    isCaptain: {
      type: Boolean,
      default: false,
    },
    isCaptainable: {
      type: Boolean,
      default: false,
    },
    isRemovable: {
      type: Boolean,
      default: false,
    },
    isClickable: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isForbidden: {
      type: Boolean,
      default: false,
    },
    isError : {
      type : Boolean,
      default : false,
    },
  },
  data() {
    return {
      isHover: false,
      cardDefault,
    };
  },
  computed: {
    widthNumber() {
      return parseInt(this.width.split('px')[0], 10);
    },
    styleCard() {
      return `width: ${this.widthNumber + 36}px`;
    },
    showCaptain() {
      if (this.$vuetify.display.xs) {
        return this.isCaptainable || this.isCaptain;
      }

      return (this.isCaptainable && this.isHover) || this.isCaptain;
    },
    cardIcon() {
      if (this.isForbidden) {
        return 'ban';
      }

      return this.isSelected ? 'check' : '';
    },
    cardHeight() {
      return this.widthNumber * 1.62;
    },
    size() {
      if (this.widthNumber >= 150) {
        return 'lg';
      }

      return 'md';
    },
  },
  methods: {
    cardClicked() {
      if (this.isClickable) {
        this.$emit('cardClicked', this.card);
      }
    },
    captainSelected() {
      if (this.isCaptainable) {
        this.$emit('captainSelected', this.card.slug);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.card-component {
  position: relative;
  margin: auto;
  padding: 12px 18px;
}

.card-content {
  position: relative;
  text-align: center;
}

.selected-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &.selected {
    border: 2px solid white;
    border-radius: 7px;
    background-color: #262626;

    .card-image {
      opacity: 0.3;
    }
  }

  .selected-icon {
    position: absolute;
  }
}

.card-image {
  overflow: visible;
  &--error{
    border: red 6px solid;
    border-radius: 12px;
  }
}

.captain {
  position: absolute;
  top: -10px;
  right: 74px;
}

.remove-card{
  position: absolute;
  top: -10px;
  left: 74px;
}

.card-score {
  position: absolute;
  bottom: -14px;
  width: 100%;
}

.last-scores {
  display: flex;
  justify-content: center;
  gap: 2px;
  padding-top: 6px;
}
</style>
